/**
 * Utilities para verificação de acesso de usuários
 */

import { supabase } from '../lib/supabase';

export interface AccessCheckResult {
  hasAccess: boolean;
  reason?: 'not_authenticated' | 'not_approved' | 'pending' | 'rejected';
  message?: string;
}

/**
 * Verifica se o usuário autenticado tem acesso aprovado à plataforma
 */
export async function checkUserAccess(): Promise<AccessCheckResult> {
  try {
    // Verificar se está autenticado
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      return {
        hasAccess: false,
        reason: 'not_authenticated',
        message: 'Usuário não autenticado',
      };
    }

    // Verificar se existe solicitação aprovada
    const { data: request, error: requestError } = await supabase
      .from('access_requests')
      .select('status')
      .eq('email', user.email)
      .single();

    // Se não encontrou registro, considerar aprovado (para retrocompatibilidade)
    // Usuários criados diretamente no Supabase Auth não terão registro aqui
    if (requestError || !request) {
      // Se a tabela não existe ou usuário não tem registro, permitir acesso
      return {
        hasAccess: true,
      };
    }

    // Verificar status da solicitação
    switch (request.status) {
      case 'approved':
        return {
          hasAccess: true,
        };
      
      case 'pending':
        return {
          hasAccess: false,
          reason: 'pending',
          message: 'Sua solicitação de acesso está aguardando aprovação do administrador.',
        };
      
      case 'rejected':
        return {
          hasAccess: false,
          reason: 'rejected',
          message: 'Sua solicitação de acesso foi rejeitada. Entre em contato com o administrador.',
        };
      
      default:
        return {
          hasAccess: false,
          reason: 'not_approved',
          message: 'Status de acesso desconhecido.',
        };
    }
  } catch (error) {
    console.error('Erro ao verificar acesso:', error);
    // Em caso de erro, permitir acesso para não bloquear o sistema
    return {
      hasAccess: true,
    };
  }
}

/**
 * Verifica se um email já possui solicitação de acesso
 */
export async function checkExistingRequest(email: string): Promise<{
  exists: boolean;
  status?: 'pending' | 'approved' | 'rejected';
}> {
  try {
    const { data, error } = await supabase
      .from('access_requests')
      .select('status')
      .eq('email', email.toLowerCase())
      .single();

    if (error || !data) {
      return { exists: false };
    }

    return {
      exists: true,
      status: data.status as 'pending' | 'approved' | 'rejected',
    };
  } catch (error) {
    console.error('Erro ao verificar solicitação existente:', error);
    return { exists: false };
  }
}
